import { useContext, useEffect, useState } from "react";
import { ClientContext } from "context/client";
import ClientService from "services/client.service";
import InputMask from "react-input-mask";


import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

import Swal from "sweetalert2";

import { useMaterialUIController } from "context";

import Dialog from "@mui/material/Dialog";

const initialState = {
    name: "",
    phone: "",
    email: "",
    document: "",
    address: "",
    neighborhood: "",
    city: "",
    state: "",
    postal_code: "",
    country: "Brasil",
    type: "",
    active: true,
};

const brazilianStates = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", 
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export default function CreateEditClientModal({ open, onClose, client, onSuccess }) {
    const { createClient, updateClient } = useContext(ClientContext);
    const [form, setForm] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const [controller] = useMaterialUIController();
    const { darkMode, sidenavColor } = controller;

    const inputStyle = {
        "& .MuiInputBase-input": { color: darkMode ? "#fff" : "inherit" },
        "& .MuiInputLabel-root": { color: darkMode ? "#fff" : "inherit" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "inherit" },
        "& .MuiSvgIcon-root": { color: darkMode ? "#fff" : "inherit" },
    };


    useEffect(() => {
        if (client) {
            const loadClientData = async () => {
                let data = client;
                
                // Se o objeto client não tiver postal_code, tentamos buscar os detalhes completos
                if (!client.postal_code && !client.address?.postal_code) {
                    try {
                        console.log("Buscando detalhes completos do cliente...");
                        const response = await ClientService.getClient(client.id);
                        data = response.data || response;
                        console.log("Dados completos recebidos:", data);
                    } catch (error) {
                        console.error("Erro ao buscar detalhes do cliente:", error);
                    }
                }

                // Verifica se o endereço vem como objeto aninhado
                const isNested = data.address && typeof data.address === 'object';
                
                // Tenta encontrar o CEP em várias propriedades possíveis (postal_code, zip_code, cep)
                const postalCode = (isNested ? data.address.postal_code : data.postal_code) || data.zip_code || data.cep || "";
                
                setForm({
                    name: data.name || "",
                    phone: data.phone || "",
                    email: data.email || "",
                    document: data.document || "",
                    address: isNested ? (data.address.address || "") : (data.address || ""),
                    neighborhood: isNested ? (data.address.neighborhood || "") : (data.neighborhood || ""),
                    city: isNested ? (data.address.city || "") : (data.city || ""),
                    state: isNested ? (data.address.state || "") : (data.state || ""),
                    postal_code: postalCode,
                    country: isNested ? (data.address.country || "Brasil") : (data.country || "Brasil"),
                    type: (data.type || "PF").toUpperCase(),
                    active: true,
                });

                const hasAddressInfo = !!(data.document || postalCode || 
                    (isNested ? data.address.address : data.address) || 
                    (isNested ? data.address.city : data.city));
                    
                setShowDetails(hasAddressInfo);
            };
            loadClientData();
        } else {
            setForm(initialState);
            setShowDetails(false);
        }
    }, [client, open]);


    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        console.log("handleSubmit - Dados do formulário a serem enviados:", form);
        setLoading(true);
        try {
            if (client) {
                await updateClient(client.id, form);
                onClose();
                await Swal.fire({
                    icon: "success",
                    title: "Cliente atualizado!",
                    timer: 1200,
                    showConfirmButton: false,
                });
            } else {
                await createClient(form);
                onClose();
                setForm(initialState);
                setShowDetails(false);
                await Swal.fire({
                    icon: "success",
                    title: "Cliente criado!",
                    timer: 1200,
                    showConfirmButton: false,
                });
            }
            onSuccess();

        } catch (error) {
            onClose();
            console.error("Erro ao salvar cliente:", error);
            Swal.fire({
                icon: "error",
                title: "Erro",
                text: "Erro ao salvar cliente",
            })
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                style: {
                    backgroundColor: darkMode ? "#202940" : "#fff",
                    color: darkMode ? "#fff" : "inherit",
                },
            }}
        >
            <MDBox p={3}>
                <MDTypography variant="h5" mb={2} color={darkMode ? "white" : "dark"}>
                    {client ? "Editar Cliente" : "Novo Cliente"}
                </MDTypography>

                <MDBox mb={2}>
                    <MDInput
                        fullWidth
                        label="Nome"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        sx={inputStyle}
                    />
                </MDBox>

                <MDBox mb={2}>
                    <InputMask
                        mask="(99) 99999-9999"
                        value={form.phone}
                        onChange={handleChange}
                        disabled={false}
                    >
                        {(inputProps) => (
                            <MDInput {...inputProps} fullWidth label="Telefone" name="phone" sx={inputStyle} />
                        )}
                    </InputMask>
                </MDBox>

                <MDBox mb={2}>
                    <MDInput
                        fullWidth
                        label="E-mail (opcional)"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        sx={inputStyle}
                    />
                </MDBox>

                <MDBox mb={2}>
                    <MDButton
                        size="small"
                        variant="text"
                        color={sidenavColor}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? "Ocultar detalhes" : "Ver detalhes (Endereço/Documento)"}
                    </MDButton>
                </MDBox>

                {showDetails && (
                    <>
                        <MDBox mb={2}>
                            <MDInput
                                select
                                fullWidth
                                label="Tipo de Pessoa"
                                name="type"
                                value={form.type}
                                onChange={handleChange}
                                SelectProps={{ native: true }}
                                sx={inputStyle}
                            >
                                <option value="PF" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Pessoa Física</option>
                                <option value="PJ" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Pessoa Jurídica</option>
                            </MDInput>
                        </MDBox>
                        <MDBox mb={2}>
                            <InputMask
                                mask={form.type === "PJ" ? "99.999.999/9999-99" : "999.999.999-99"}
                                value={form.document}
                                onChange={handleChange}
                                disabled={false}
                            >
                                {(inputProps) => (
                                    <MDInput 
                                        {...inputProps} 
                                        fullWidth 
                                        label={form.type === "PJ" ? "CNPJ" : "CPF"} 
                                        name="document" 
                                        sx={inputStyle} 
                                    />
                                )}
                            </InputMask>
                        </MDBox>
                        <MDBox mb={2}>
                            <InputMask
                                mask="99999-999"
                                value={form.postal_code}
                                onChange={handleChange}
                                disabled={false}
                            >
                                {(inputProps) => (
                                    <MDInput 
                                        {...inputProps} 
                                        fullWidth 
                                        label="CEP" 
                                        name="postal_code" 
                                        sx={inputStyle} 
                                    />
                                )}
                            </InputMask>
                        </MDBox>
                        <MDBox mb={2}>
                            <MDInput
                                fullWidth
                                label="Endereço"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                sx={inputStyle}
                            />
                        </MDBox>
                        <MDBox mb={2}>
                            <MDInput
                                fullWidth
                                label="Bairro"
                                name="neighborhood"
                                value={form.neighborhood}
                                onChange={handleChange}
                                sx={inputStyle}
                            />
                        </MDBox>
                        <MDBox display="flex" gap={2} mb={2}>
                            <MDInput
                                fullWidth
                                label="Cidade"
                                name="city"
                                value={form.city}
                                onChange={handleChange}
                                sx={inputStyle}
                            />
                            <MDInput
                                select
                                fullWidth
                                label="Estado"
                                name="state"
                                value={form.state}
                                onChange={handleChange}
                                SelectProps={{ native: true }}
                                sx={inputStyle}
                            >
                                <option value="" style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>Selecione</option>
                                {brazilianStates.map((uf) => (
                                    <option key={uf} value={uf} style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }}>{uf}</option>
                                ))}
                            </MDInput>
                        </MDBox>
                    </>
                )}

                <MDBox display="flex" justifyContent="flex-end" gap={1}>
                    <MDButton variant="text" color="secondary" onClick={onClose}>
                        Cancelar
                    </MDButton>

                    <MDButton
                        variant="gradient"
                        color={sidenavColor}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Salvando..." : (client ? "Salvar" : "Criar")}
                    </MDButton>
                </MDBox>
            </MDBox>
        </Dialog>
    );
}
